import { ArrowDown, ArrowUp, Check, Circle } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import {
  useAccounts,
  useBudgetStatus,
  useCashFlow,
  useCategories,
  useMonthlySummary,
  useRecurrences,
  useTransactionPages,
} from "@/api/finance";
import type { Account } from "@/api/types";
import { BudgetProgress } from "@/components/BudgetProgress";
import { CategoryIcon } from "@/components/CategoryIcon";
import { Donut, SWATCH } from "@/components/Donut";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ErrorBanner, Loading } from "@/components/ui/feedback";
import { OtherCurrencyTotalsList } from "@/features/reports/OtherCurrencies";
import { useOpenQuickAdd } from "@/layout/QuickAdd";
import { budgetAlerts, monthComparison, topCategories } from "@/lib/dashboard";
import { addMonths, currentMonth, formatMonth, shortDay, todayCivil } from "@/lib/dates";
import { formatMoney } from "@/lib/money";
import { upcoming } from "@/lib/recurrence";
import { cn } from "@/lib/utils";
import { useCan, useWorkspace } from "../../layout/workspace-context";
import { TransactionItem } from "../transactions/TransactionItem";

/** Carregando → Loading; erro → ErrorBanner; só depois o conteúdo (que pode ser o estado vazio). */
function Guard({
  queries,
  children,
}: {
  queries: Array<{ isPending: boolean; isError: boolean; error: unknown }>;
  children: ReactNode;
}) {
  const failed = queries.find((q) => q.isError);
  if (failed) return <ErrorBanner error={failed.error} />;
  if (queries.some((q) => q.isPending)) return <Loading />;
  return <>{children}</>;
}

function CardLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-sm font-medium text-link hover:underline">
      {children}
    </Link>
  );
}

const monthName = (month: string) => formatMonth(month).replace(/ de \d{4}$/, "");

function Delta({ pct, previous, goodWhenUp }: { pct: number | null; previous: string; goodWhenUp: boolean }) {
  if (pct === null) return null;
  if (pct === 0)
    return <p className="m-0 mt-1 text-xs font-medium text-muted">igual a {monthName(previous)}</p>;
  const up = pct > 0;
  const good = up === goodWhenUp;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <p
      className={cn(
        "mt-1 flex items-center gap-1 text-xs font-medium",
        good ? "text-income" : "text-expense",
      )}
    >
      <Icon aria-hidden="true" className="size-3.5" />
      {Math.abs(pct)}% {up ? "a mais" : "a menos"} que em {monthName(previous)}
    </p>
  );
}

function Hero({ accounts, currency }: { accounts: Account[]; currency: string }) {
  // Sem conversão de moedas (fora do escopo): o total é só das contas na moeda do workspace; as demais à parte.
  const total = accounts.filter((a) => a.currency === currency).reduce((s, a) => s + a.balanceMinor, 0);
  const others = new Map<string, number>();
  for (const a of accounts)
    if (a.currency !== currency) others.set(a.currency, (others.get(a.currency) ?? 0) + a.balanceMinor);
  return (
    <section
      aria-label="Saldo total"
      className="relative flex min-w-0 flex-col overflow-hidden rounded-hero bg-accent p-6 text-accent-ink shadow-card lg:col-span-2 lg:p-7"
    >
      <div
        aria-hidden="true"
        className="absolute -top-24 -right-20 size-72 rounded-full border-[48px] border-accent-ink/10"
      />
      <p className="relative m-0 text-sm text-accent-ink/85">
        Saldo total · {accounts.length} {accounts.length === 1 ? "conta" : "contas"}
      </p>
      <p className="relative m-0 mt-1 text-[38px] leading-tight font-bold tracking-[-0.035em] tabular-nums lg:text-5xl">
        {formatMoney(total, currency)}
      </p>
      {others.size > 0 && (
        <p className="relative m-0 mt-1 text-sm text-accent-ink/85 tabular-nums">
          Em outras moedas: {[...others].map(([cur, minor]) => formatMoney(minor, cur)).join(" · ")}
        </p>
      )}
      {total < 0 && (
        <p className="relative m-0 mt-2 inline-block rounded-full bg-surface px-2.5 py-0.5 text-xs font-semibold text-expense">
          Saldo negativo
        </p>
      )}
      <ul className="no-scrollbar relative m-0 mt-5 flex list-none gap-2 overflow-x-auto p-0 lg:mt-auto lg:pt-6">
        {accounts.map((a) => (
          <li key={a.id} className="min-w-32 shrink-0 rounded-chip bg-accent-ink/15 px-3.5 py-2.5">
            <span className="block truncate text-xs text-accent-ink/85">{a.name}</span>
            <span className="block text-[15px] font-semibold tabular-nums">
              {formatMoney(a.balanceMinor, a.currency)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** `hasBudget` null = ainda carregando (o passo opcional não aparece até saber). */
function Onboarding({ hasAccount, hasBudget }: { hasAccount: boolean; hasBudget: boolean | null }) {
  const ws = useWorkspace();
  const openQuickAdd = useOpenQuickAdd();
  const canWrite = useCan("transaction:write");
  const step = (done: boolean, label: string, doneLabel: string, action: ReactNode) => (
    <li className="flex items-center gap-3 py-3">
      {done ? (
        <Check aria-hidden="true" className="size-6 shrink-0 rounded-full bg-income-soft p-1 text-income" />
      ) : (
        <Circle aria-hidden="true" className="size-6 shrink-0 text-border" />
      )}
      <span className={cn("flex-1 text-[15px]", done && "text-muted line-through")}>
        {done ? doneLabel : label}
      </span>
      {!done && action}
    </li>
  );
  return (
    <Card title="Primeiros passos" className="lg:col-span-3">
      <ol className="m-0 list-none divide-y divide-border p-0">
        {step(
          hasAccount,
          "Crie sua primeira conta (corrente, cartão, carteira…)",
          "Conta criada",
          <Button asChild size="md">
            <Link to={`/w/${ws.id}/accounts`}>Criar conta</Link>
          </Button>,
        )}
        {hasBudget !== null &&
          step(
            hasBudget,
            "Opcional: defina um orçamento para uma categoria",
            "Orçamento definido",
            hasAccount && (
              <Button asChild variant="secondary">
                <Link to={`/w/${ws.id}/budgets`}>Definir</Link>
              </Button>
            ),
          )}
        {step(
          false,
          "Registre seu primeiro lançamento",
          "",
          hasAccount && canWrite && <Button onClick={openQuickAdd}>Fazer o primeiro lançamento</Button>,
        )}
      </ol>
    </Card>
  );
}

export function DashboardPage() {
  const ws = useWorkspace();
  const month = currentMonth(ws.timezone);
  const previous = addMonths(month, -1);
  const today = todayCivil(ws.timezone);
  const accounts = useAccounts(ws.id);
  const allAccounts = useAccounts(ws.id, true); // inclui arquivadas: recorrências podem apontar para elas
  const categories = useCategories(ws.id);
  const summary = useMonthlySummary(ws.id, month);
  const cashFlow = useCashFlow(ws.id, previous, month);
  const budgets = useBudgetStatus(ws.id, month);
  const recurrences = useRecurrences(ws.id);
  const recent = useTransactionPages(ws.id, {}, 5);
  const base = `/w/${ws.id}`;

  if (accounts.isPending) return <Loading />;
  if (accounts.isError) return <ErrorBanner error={accounts.error} />;

  const firstPage = recent.data?.pages[0]?.transactions;
  const showOnboarding = accounts.data.length === 0 || (firstPage !== undefined && firstPage.length === 0);
  const flow = cashFlow.data;
  const current = flow?.find((m) => m.month === month);
  const prev = flow?.find((m) => m.month === previous);
  const prevHasData = prev && (prev.incomeMinor > 0 || prev.expenseMinor > 0) ? prev : undefined;
  const comparison = current ? monthComparison(current, prevHasData) : null;
  const slices = summary.data && current ? topCategories(summary.data, current.expenseMinor) : [];
  const sliceTotal = slices.reduce((s, x) => s + x.valueMinor, 0);
  const alerts = budgets.data ? budgetAlerts(budgets.data).slice(0, 4) : [];
  const next = recurrences.data ? upcoming(recurrences.data, today, 7) : [];
  const categoryOf = (id: string | null) => categories.data?.find((c) => c.id === id) ?? null;
  const currencyOf = (accountId: string) =>
    allAccounts.data?.find((a) => a.id === accountId)?.currency ?? ws.defaultCurrency;

  return (
    <>
      <h1 className="mt-0 mb-5 text-[28px] font-bold tracking-[-0.03em]">Início</h1>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-5">
        {showOnboarding && (
          <Onboarding
            hasAccount={accounts.data.length > 0}
            hasBudget={budgets.isPending ? null : Boolean(budgets.data?.length)}
          />
        )}
        {accounts.data.length > 0 && (
          <>
            <Hero accounts={accounts.data} currency={ws.defaultCurrency} />

            <Card title={formatMonth(month)}>
              {comparison ? (
                <div className="flex flex-col gap-2.5">
                  {/* biome-ignore lint/a11y/useSemanticElements: agrupa rótulo, valor e variação fora de formulário — <fieldset> é de formulário */}
                  <div role="group" aria-label="Receitas" className="rounded-chip bg-income-soft px-4 py-3">
                    <p className="m-0 flex items-center gap-1.5 text-sm text-income">
                      <ArrowUp aria-hidden="true" className="size-3.5" />
                      Receitas
                    </p>
                    <p className="m-0 text-[22px] font-semibold tabular-nums">
                      {formatMoney(comparison.income.valueMinor, ws.defaultCurrency)}
                    </p>
                    <Delta pct={comparison.income.deltaPct} previous={previous} goodWhenUp />
                  </div>
                  {/* biome-ignore lint/a11y/useSemanticElements: agrupa rótulo, valor e variação fora de formulário — <fieldset> é de formulário */}
                  <div role="group" aria-label="Despesas" className="rounded-chip bg-expense-soft px-4 py-3">
                    <p className="m-0 flex items-center gap-1.5 text-sm text-expense">
                      <ArrowDown aria-hidden="true" className="size-3.5" />
                      Despesas
                    </p>
                    <p className="m-0 text-[22px] font-semibold tabular-nums">
                      {formatMoney(comparison.expense.valueMinor, ws.defaultCurrency)}
                    </p>
                    <Delta pct={comparison.expense.deltaPct} previous={previous} goodWhenUp={false} />
                  </div>
                  {/* biome-ignore lint/a11y/useSemanticElements: agrupa rótulo, valor e variação fora de formulário — <fieldset> é de formulário */}
                  <div
                    role="group"
                    aria-label="Resultado do mês"
                    className="flex items-center justify-between gap-3 px-1 pt-1"
                  >
                    <span className="text-sm text-muted">Resultado do mês</span>
                    <span
                      className={cn(
                        "text-lg font-semibold tabular-nums",
                        comparison.netMinor >= 0 ? "text-income" : "text-expense",
                      )}
                    >
                      {formatMoney(comparison.netMinor, ws.defaultCurrency)}
                    </span>
                  </div>
                  {current && <OtherCurrencyTotalsList totals={current.otherCurrencies} />}
                </div>
              ) : cashFlow.isError ? (
                <ErrorBanner error={cashFlow.error} />
              ) : (
                <Loading />
              )}
            </Card>

            <Card
              title="Para onde foi o dinheiro"
              action={<CardLink to={`${base}/reports`}>Relatórios</CardLink>}
            >
              <Guard queries={[summary, cashFlow]}>
                {slices.length ? (
                  <div className="flex flex-wrap items-center gap-5">
                    <Donut slices={slices} label={`Despesas de ${formatMonth(month)} por categoria`}>
                      <span className="text-xs text-muted">gasto</span>
                      <span className="text-sm font-semibold tabular-nums">
                        {formatMoney(sliceTotal, ws.defaultCurrency)}
                      </span>
                    </Donut>
                    <ul className="m-0 flex min-w-44 flex-1 list-none flex-col gap-2.5 p-0 text-sm">
                      {slices.map((s) => (
                        <li key={s.key} className="flex items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-2">
                            <span
                              aria-hidden="true"
                              className={cn("size-2.5 shrink-0 rounded-[3px]", SWATCH[s.color])}
                            />
                            <span className="truncate">{s.name}</span>
                          </span>
                          <span className="shrink-0 font-semibold tabular-nums">
                            {formatMoney(s.valueMinor, ws.defaultCurrency)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="m-0 text-sm text-muted">Nenhuma despesa neste mês.</p>
                )}
              </Guard>
            </Card>

            <Card title="Orçamentos" action={<CardLink to={`${base}/budgets`}>Ver todos</CardLink>}>
              <Guard queries={[budgets]}>
                {alerts.length ? (
                  <div className="flex flex-col gap-4">
                    {alerts.map((b) => (
                      <BudgetProgress
                        key={b.categoryId}
                        name={b.categoryName}
                        spentMinor={b.spentMinor}
                        budgetedMinor={b.budgetedMinor}
                        percentUsed={b.percentUsed}
                        level={b.level}
                        currency={ws.defaultCurrency}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="m-0 text-sm text-muted">
                    Nenhum orçamento definido. <CardLink to={`${base}/budgets`}>Definir</CardLink>
                  </p>
                )}
              </Guard>
            </Card>

            <Card
              title="Próximos 7 dias"
              action={<CardLink to={`${base}/recurrences`}>Recorrências</CardLink>}
            >
              <Guard queries={[recurrences]}>
                {next.length ? (
                  <ul className="m-0 list-none divide-y divide-border p-0">
                    {next.map(({ recurrence: r, date }) => (
                      <li key={`${r.id}-${date}`} className="flex items-center justify-between gap-3 py-2.5">
                        <span className="flex min-w-0 items-center gap-3">
                          <CategoryIcon category={categoryOf(r.categoryId)} />
                          <span className="flex min-w-0 flex-col">
                            <span className="truncate text-[15px] font-medium">{r.description}</span>
                            <span className="text-xs text-muted">{shortDay(date)}</span>
                          </span>
                        </span>
                        <span
                          className={cn(
                            "shrink-0 font-semibold tabular-nums",
                            r.type === "income" ? "text-income" : "text-expense",
                          )}
                        >
                          {r.type === "income" ? "+ " : "− "}
                          {formatMoney(r.amountMinor, currencyOf(r.accountId))}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="m-0 text-sm text-muted">Nada programado para os próximos 7 dias.</p>
                )}
              </Guard>
            </Card>

            <Card
              title="Últimos lançamentos"
              action={<CardLink to={`${base}/transactions`}>Ver todos</CardLink>}
              className="lg:col-span-2"
            >
              {recent.isError ? (
                <ErrorBanner error={recent.error} />
              ) : firstPage?.length ? (
                <ul className="m-0 list-none p-0">
                  {firstPage.map((tx) => (
                    <TransactionItem
                      key={tx.id}
                      tx={tx}
                      wid={ws.id}
                      accounts={accounts.data}
                      categories={categories.data}
                    />
                  ))}
                </ul>
              ) : (
                <p className="m-0 text-sm text-muted">Nenhum lançamento ainda.</p>
              )}
            </Card>
          </>
        )}
      </div>
    </>
  );
}
